import {
    ActivityType,
    ChallengeActivityFilterDto,
    ChallengeAggregationType,
    ChallengeDisplayNormalizationFormulaDto,
    ChallengeFitnessMetricType,
    ChallengeInitialResultMode,
    ChallengeRuleType
} from '@core/api/ChallengeClient';
import { WorkoutCategory } from '@api/FitnessActivityClient';
import { ChallengeRulesFormType } from './store';
import { normalizeChallengeActivityFilters } from './activity-filters';
import { isRawValueRuleType, supportsActivityFilters } from '../../rule-types';

export interface ChallengeRulePayload {
    activityType?: ActivityType;
    workoutCategory?: WorkoutCategory;
    compositePointTypeKey?: string;
    ruleType: ChallengeRuleType;
    fitnessMetricType?: ChallengeFitnessMetricType;
    calculatedMetricKey?: string;
    aggregationType: ChallengeAggregationType;
    initialResultMode: ChallengeInitialResultMode;
    resultBaseline?: number;
    displayNormalizationFormula?: ChallengeDisplayNormalizationFormulaDto;
    activityFilters: ChallengeActivityFilterDto[];
}

const buildDisplayNormalizationFormula = (
    form: ChallengeRulesFormType
): ChallengeDisplayNormalizationFormulaDto | undefined => {
    const operation = form.$.normalizationOperation.value;
    const operand = form.$.normalizationOperand.value;

    if (
        !isRawValueRuleType(form.$.ruleType.value) ||
        !operation ||
        !operand ||
        operand <= 0
    ) {
        return undefined;
    }

    return {
        operation,
        operand,
        outputFormat: form.$.normalizationOutputFormat.value
    };
};

export const buildChallengeRulePayload = (
    form: ChallengeRulesFormType
): ChallengeRulePayload => {
    const useComposite = form.$.useCompositePoints.value;
    const ruleType = form.$.ruleType.value;
    const isRawValue = isRawValueRuleType(ruleType);
    const usesCalculatedMetric =
        isRawValue &&
        form.$.fitnessMetricType.value ===
            ChallengeFitnessMetricType.WORKOUT_CALCULATED_METRIC;
    const activityType = useComposite ? undefined : form.$.activityType.value;
    const workoutCategory =
        activityType === ActivityType.WORKOUT
            ? form.$.workoutCategory.value
            : undefined;

    return {
        activityType,
        workoutCategory,
        compositePointTypeKey: useComposite
            ? form.$.compositePointTypeKey.value
            : undefined,
        ruleType,
        fitnessMetricType: isRawValue
            ? form.$.fitnessMetricType.value
            : undefined,
        calculatedMetricKey: usesCalculatedMetric
            ? form.$.calculatedMetricKey.value
            : undefined,
        aggregationType: isRawValue
            ? form.$.aggregationType.value
            : ChallengeAggregationType.SUM,
        initialResultMode:
            ruleType === ChallengeRuleType.ACTIVITY_POINTS
                ? form.$.initialResultMode.value
                : ChallengeInitialResultMode.INCREMENTAL,
        resultBaseline: form.$.resultBaseline.value,
        displayNormalizationFormula: buildDisplayNormalizationFormula(form),
        activityFilters: supportsActivityFilters(ruleType)
            ? normalizeChallengeActivityFilters(form.$.activityFilters.value)
            : []
    };
};
